import React from "react";
import { Microscope, Scale, Activity, Users, Network, Layers, Stethoscope, GitBranch } from "lucide-react";
import { useLang } from "../context/LangContext";
import { FadeUp, Stagger, StaggerItem } from "../components/Motion";

const dimIcons = [Microscope, Scale, Activity, Users, Network];
const cellIcons = [Layers, GitBranch, Stethoscope];

export default function About() {
  const { t } = useLang();
  const dims = [
    { h: t.about.dim_1_title, d: t.about.dim_1_desc },
    { h: t.about.dim_2_title, d: t.about.dim_2_desc },
    { h: t.about.dim_3_title, d: t.about.dim_3_desc },
    { h: t.about.dim_4_title, d: t.about.dim_4_desc },
    { h: t.about.dim_5_title, d: t.about.dim_5_desc },
  ];
  const cells = [
    { h: t.about.cell_1_title, d: t.about.cell_1_desc },
    { h: t.about.cell_2_title, d: t.about.cell_2_desc },
    { h: t.about.cell_3_title, d: t.about.cell_3_desc },
  ];

  return (
    <div data-testid="page-about">
      {/* HEADER */}
      <section className="section-pad">
        <div className="page-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <p className="eyebrow mb-5">{t.about.eyebrow}</p>
            <h1 className="display-heading text-4xl md:text-5xl lg:text-6xl accent-rule">{t.about.title}</h1>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-8">
            <p className="text-base md:text-lg text-text-muted leading-relaxed">{t.about.lead}</p>
          </FadeUp>
        </div>
      </section>

      <div className="page-container"><div className="h-px bg-rule" /></div>

      {/* 5 DIMENSIONS */}
      <section className="section-pad bg-pearl">
        <div className="page-container">
          <FadeUp>
            <p className="eyebrow mb-5">001 — METHODOLOGY</p>
            <h2 className="display-heading text-3xl md:text-5xl max-w-3xl accent-rule">{t.about.methodology_title}</h2>
            <p className="mt-10 text-text-muted text-base md:text-lg max-w-3xl leading-relaxed">{t.about.methodology_sub}</p>
          </FadeUp>

          {/* Visual ring infographic */}
          <FadeUp delay={0.1}>
            <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5">
                <DimensionDiagram />
              </div>
              <Stagger className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
                {dims.map((d, i) => {
                  const Icon = dimIcons[i];
                  return (
                    <StaggerItem key={i}>
                      <div className="bg-white border border-rule p-7 h-full mosaic-card">
                        <div className="flex items-start gap-4">
                          <div className="shrink-0 w-10 h-10 flex items-center justify-center border border-rule">
                            <Icon className="w-4.5 h-4.5 text-cerulean" strokeWidth={1.4} />
                          </div>
                          <div>
                            <div className="text-[10px] font-mono tracking-[0.22em] text-cerulean">DIM. {String(i + 1).padStart(2, "0")}</div>
                            <h3 className="display-heading text-lg mt-2">{d.h}</h3>
                            <p className="mt-3 text-sm text-text-muted leading-relaxed">{d.d}</p>
                          </div>
                        </div>
                      </div>
                    </StaggerItem>
                  );
                })}
              </Stagger>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* OPERATING MODEL */}
      <section className="section-pad">
        <div className="page-container">
          <FadeUp>
            <p className="eyebrow mb-5">002 — OPERATING MODEL</p>
            <h2 className="display-heading text-3xl md:text-5xl max-w-3xl accent-rule">{t.about.operating_title}</h2>
            <p className="mt-10 text-text-muted text-base md:text-lg max-w-3xl leading-relaxed">{t.about.operating_sub}</p>
          </FadeUp>

          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {cells.map((c, i) => {
              const Icon = cellIcons[i];
              return (
                <StaggerItem key={i}>
                  <div className="bg-white border border-rule p-8 h-full mosaic-card">
                    <Icon className="w-7 h-7 text-cerulean" strokeWidth={1.3} />
                    <div className="mt-6 text-[10px] font-mono tracking-[0.22em] text-cerulean">CELL {String(i + 1).padStart(2, "0")}</div>
                    <h3 className="mt-3 display-heading text-xl">{c.h}</h3>
                    <p className="mt-4 text-sm text-text-muted leading-relaxed">{c.d}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>
    </div>
  );
}

function DimensionDiagram() {
  // 5-point pentagonal vector diagram
  const cx = 150, cy = 150, r = 110;
  const points = Array.from({ length: 5 }, (_, i) => {
    const ang = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
    return { x: cx + r * Math.cos(ang), y: cy + r * Math.sin(ang), label: `0${i + 1}` };
  });
  return (
    <svg viewBox="0 0 300 300" className="w-full max-w-[420px] mx-auto">
      <circle cx={cx} cy={cy} r={r + 22} stroke="#E2E8F0" strokeWidth="0.75" fill="none" />
      <circle cx={cx} cy={cy} r={r} stroke="#E2E8F0" strokeWidth="0.75" fill="none" />
      <circle cx={cx} cy={cy} r={r - 36} stroke="#E2E8F0" strokeWidth="0.75" fill="none" />
      {points.map((p, i) => (
        <line key={`l-${i}`} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#E2E8F0" strokeWidth="0.75" />
      ))}
      <polygon
        points={points.map((p) => `${p.x},${p.y}`).join(" ")}
        fill="rgba(0,119,182,0.06)"
        stroke="#0077B6"
        strokeWidth="1"
      />
      {points.map((p, i) => (
        <g key={`pt-${i}`}>
          <circle cx={p.x} cy={p.y} r={6} fill="#08183A" />
          <circle cx={p.x} cy={p.y} r={2.4} fill="#CAF0F8" />
          <text
            x={p.x}
            y={p.y - 14}
            textAnchor="middle"
            className="fill-[#0077B6]"
            fontFamily="JetBrains Mono, monospace"
            fontSize="10"
            letterSpacing="2"
          >
            {p.label}
          </text>
        </g>
      ))}
      <text x={cx} y={cy - 4} textAnchor="middle" fontFamily="Montserrat, sans-serif" fontSize="13" fontWeight="600" fill="#08183A">
        CONVERGENCIA
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#475569" letterSpacing="2">
        5 DIMENSIONS
      </text>
    </svg>
  );
}
